import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { listMembers } from '../../api/portal'
import { logout } from '../../auth/session'
import { usePortal } from '../../auth/sessionContext'
import { clearSelectedCustomer } from '../../auth/selectedCustomer'
import { DataState } from '../../components/DataState'
import { Fact } from '../../components/ui/Fact'
import { Ltr } from '../../components/ui/Ltr'
import { PageHeader } from '../../components/ui/PageHeader'
import { SectionCard } from '../../components/ui/SectionCard'
import { StatusChip } from '../../components/ui/StatusChip'
import { formatDateTime } from '../../format'
import { roleLabel } from '../../vocabulary'

/**
 * Who you are, which account you are in, and who else is in it.
 *
 * Informational by design. Adding a member, changing a role or revoking access
 * would be granting spending authority over the account being billed, from
 * inside it — that decision belongs to the operator, and this page says where
 * to ask rather than showing buttons that would be refused.
 *
 * The member list used to be a destination of its own in the sidebar, which
 * made a read-only list look like a management screen. It is a fact about this
 * account, so it lives with the other facts about this account.
 */
export function AccountPage() {
  const portal = usePortal()
  const queryClient = useQueryClient()
  const { user, memberships } = portal.session
  const current = portal.membership
  const many = memberships.length > 1

  const members = useQuery({
    queryKey: ['client', 'members', current.customer.id],
    queryFn: ({ signal }) => listMembers(current.customer.id, signal),
  })

  const signOut = useMutation({
    mutationFn: logout,
    onSettled: () => {
      // The remembered account is a hint about where to open next time, not a
      // credential — but leaving it behind on a shared machine would show the
      // next person which customer somebody worked in.
      clearSelectedCustomer()
      void queryClient.resetQueries()
    },
  })

  return (
    <Box>
      <PageHeader
        title="حساب کاربری"
        description="شمارهٔ موبایلی که با آن وارد شده‌اید، حسابی که در آن هستید، و همکارانی که به همین حساب دسترسی دارند."
      />

      <Stack spacing={3}>
        <SectionCard title="ورود شما">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={4}
            sx={{ alignItems: { sm: 'flex-start' } }}
          >
            <Fact label="شمارهٔ موبایل">
              <Ltr mono>{user.mobile ?? '—'}</Ltr>
            </Fact>
            {user.full_name ? <Fact label="نام">{user.full_name}</Fact> : null}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, maxWidth: 640 }}>
            ورود با همین شماره و کد پیامکی انجام می‌شود. گذرواژه‌ای در کار نیست، بنابراین چیزی
            برای تغییر یا فراموش‌کردن وجود ندارد. برای تغییر شماره با پشتیبانی تماس بگیرید.
          </Typography>
        </SectionCard>

        <SectionCard
          title="حساب فعلی"
          action={
            many ? (
              <TextField
                select
                size="small"
                label="تغییر حساب"
                value={current.customer.id}
                onChange={(event) => portal.selectCustomer(event.target.value)}
                sx={{ minWidth: 220 }}
              >
                {memberships.map((row) => (
                  <MenuItem key={row.customer.id} value={row.customer.id}>
                    {row.customer.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : null
          }
        >
          <Stack direction="row" spacing={4} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Fact label="حساب">{current.customer.name}</Fact>
            <Fact label="نقش شما">{roleLabel(current.role)}</Fact>
            <Fact label="وضعیت">
              <StatusChip status={current.customer.status} />
            </Fact>
          </Stack>
          {many ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              شما به {memberships.length} حساب دسترسی دارید. آنچه در بقیهٔ صفحه‌ها می‌بینید مربوط
              به حساب انتخاب‌شده در بالاست.
            </Typography>
          ) : null}
        </SectionCard>

        <SectionCard
          title="اعضای این حساب"
          description="افزودن عضو تازه، تغییر نقش‌ها و لغو دسترسی از سمت ما انجام می‌شود. برای هر کدام با پشتیبانی تماس بگیرید."
          flush
        >
          <DataState
            isPending={members.isPending}
            isError={members.isError}
            error={members.error}
            isEmpty={members.data?.length === 0}
            emptyMessage="هیچ عضو دیگری برای این حساب ثبت نشده است."
            onRetry={() => void members.refetch()}
          >
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="اعضای این حساب">
                <TableHead>
                  <TableRow>
                    <TableCell>نام</TableCell>
                    <TableCell>شمارهٔ موبایل</TableCell>
                    <TableCell>نقش</TableCell>
                    <TableCell>وضعیت</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      افزوده‌شده
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.data?.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.full_name || '—'}</TableCell>
                      <TableCell>
                        <Ltr mono variant="body2">
                          {row.mobile || '—'}
                        </Ltr>
                      </TableCell>
                      <TableCell>{roleLabel(row.role)}</TableCell>
                      <TableCell>
                        {!row.is_active ? (
                          <Chip size="small" label="دسترسی لغو شده" />
                        ) : row.has_signed_in ? (
                          <Chip size="small" color="success" label="فعال" />
                        ) : (
                          <Chip size="small" color="warning" label="هنوز وارد نشده" />
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {formatDateTime(row.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DataState>
        </SectionCard>

        <Box>
          <Button
            variant="outlined"
            color="error"
            disabled={signOut.isPending}
            onClick={() => signOut.mutate()}
          >
            خروج از حساب
          </Button>
        </Box>
      </Stack>
    </Box>
  )
}
